import { useEffect, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DASHBOARD_ROLLUPS_COLLECTION,
  type OverviewMonthRollup,
  type OverviewRangeMetrics,
  avgDaysToSellFromRollup,
  overviewMonthRollupDocId,
  periodKeysOverlappingRange,
  rangeIsFullLocalMonths,
  sumOverviewMonthRollups,
} from '@otw/models'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from 'config/firebase'
import { useAuth } from 'contexts/AuthContext'
import { axiosClient } from 'core/api/axiosClient'
import { overviewFinancialQueryOptions } from './dashboardQueryOptions'
import type { DashboardDateRange } from './useDashboardVehicles'
import { computeOverviewMonthRollup } from './computeOverviewMonthRollup'
import type { Expense, Vehicle } from '@otw/models'

const ROLLUP_QUERY_ROOT = 'dashboard-rollup' as const
const BACKFILL_STORAGE_KEY = 'otw-overview-rollup-backfill-at'
const BACKFILL_MIN_INTERVAL_MS = 5 * 60 * 1000

export type OverviewFinancialMetrics = OverviewRangeMetrics

export const overviewRollupQueryKey = (startMs: number, endMs: number) =>
  [ROLLUP_QUERY_ROOT, startMs, endMs] as const

const parseRollup = (data: unknown, periodKey: string): OverviewMonthRollup | null => {
  if (!data || typeof data !== 'object') return null
  const raw = data as Partial<OverviewMonthRollup>
  if (typeof raw.soldCount !== 'number' || typeof raw.totalRevenue !== 'number') return null
  return {
    periodKey: raw.periodKey || periodKey,
    startMs: Number(raw.startMs) || 0,
    endMs: Number(raw.endMs) || 0,
    soldCount: raw.soldCount,
    totalRevenue: raw.totalRevenue,
    totalProfit: Number(raw.totalProfit) || 0,
    daysToSellSum: Number(raw.daysToSellSum) || 0,
    expenseTotal: Number(raw.expenseTotal) || 0,
    expensesByCategory: (raw.expensesByCategory as Record<string, number>) ?? {},
    expenseLinkedVehicleCounts: (raw.expenseLinkedVehicleCounts as Record<string, number>) ?? {},
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : new Date().toISOString(),
  }
}

const fetchFirestoreMonthRollups = async (
  periodKeys: string[],
): Promise<OverviewFinancialMetrics | null> => {
  if (periodKeys.length === 0) return null
  const months: OverviewMonthRollup[] = []
  for (const periodKey of periodKeys) {
    const snap = await getDoc(doc(db, DASHBOARD_ROLLUPS_COLLECTION, overviewMonthRollupDocId(periodKey)))
    if (!snap.exists()) return null
    const parsed = parseRollup(snap.data(), periodKey)
    if (!parsed) return null
    months.push(parsed)
  }
  const summed = sumOverviewMonthRollups(months)
  return {
    ...summed,
    avgDaysToSell: avgDaysToSellFromRollup(summed.soldCount, summed.daysToSellSum),
    roi: summed.totalRevenue > 0 ? (summed.totalProfit / summed.totalRevenue) * 100 : 0,
  }
}

type OverviewApiResponse = OverviewFinancialMetrics & {
  startMs: number
  endMs: number
  updatedAt?: string
  cached?: boolean
  source?: string
}

/**
 * Prefers Express API overview totals (in-process cache on server). Soft-falls back
 * to Firestore month rollups, then null so callers use sold/expense queries.
 */
export const useOverviewFinancialRollup = (dateRange: DashboardDateRange) => {
  const startMs = dateRange.start.getTime()
  const endMs = dateRange.end.getTime()
  const canUseRollup = rangeIsFullLocalMonths(startMs, endMs)
  const periodKeys = useMemo(
    () => (canUseRollup ? periodKeysOverlappingRange(startMs, endMs) : []),
    [canUseRollup, startMs, endMs],
  )

  const query = useQuery({
    queryKey: overviewRollupQueryKey(startMs, endMs),
    enabled: Number.isFinite(startMs) && Number.isFinite(endMs) && endMs >= startMs,
    queryFn: async (): Promise<OverviewFinancialMetrics> => {
      try {
        const data = (await axiosClient.get('/portal/admin/dashboard/overview', {
          params: { startMs, endMs },
        })) as OverviewApiResponse
        if (typeof data?.soldCount !== 'number' || typeof data?.totalRevenue !== 'number') {
          throw new Error('Invalid overview API payload')
        }
        return {
          soldCount: data.soldCount,
          totalRevenue: data.totalRevenue,
          totalProfit: Number(data.totalProfit) || 0,
          daysToSellSum: Number(data.daysToSellSum) || 0,
          expenseTotal: Number(data.expenseTotal) || 0,
          expensesByCategory: data.expensesByCategory ?? {},
          expenseLinkedVehicleCounts: data.expenseLinkedVehicleCounts ?? {},
          avgDaysToSell:
            typeof data.avgDaysToSell === 'number'
              ? data.avgDaysToSell
              : avgDaysToSellFromRollup(data.soldCount, Number(data.daysToSellSum) || 0),
          roi:
            typeof data.roi === 'number'
              ? data.roi
              : data.totalRevenue > 0
                ? ((Number(data.totalProfit) || 0) / data.totalRevenue) * 100
                : 0,
        }
      } catch (apiError) {
        // Never succeed with null — that gets persisted and blocks hydrate TTFD on refresh.
        if (!canUseRollup) throw apiError
        const fromFirestore = await fetchFirestoreMonthRollups(periodKeys)
        if (!fromFirestore) throw apiError
        return fromFirestore
      }
    },
    ...overviewFinancialQueryOptions,
  })

  const metrics = query.data ?? null

  return {
    metrics,
    periodKeys,
    canUseRollup,
    // Hydrated sessionStorage data must paint immediately (isFetching may still be true).
    isLoading: metrics == null && (query.isLoading || query.isPending),
    isFetching: query.isFetching,
    hasRollup: metrics != null,
  }
}

/** Owner-throttled Firestore backfill when API/Firestore rollups are missing but live data is loaded. */
export const useOverviewRollupBackfill = ({
  dateRange,
  hasRollup,
  canUseRollup,
  vehicles,
  expenses,
}: {
  dateRange: DashboardDateRange
  hasRollup: boolean
  canUseRollup: boolean
  vehicles?: Vehicle[]
  expenses?: Expense[]
}) => {
  const { userInfo } = useAuth()
  const queryClient = useQueryClient()
  const startMs = dateRange.start.getTime()
  const endMs = dateRange.end.getTime()

  useEffect(() => {
    if (!canUseRollup || hasRollup) return
    if (!userInfo?.isOwner) return
    if (!vehicles?.length && !expenses?.length) return

    try {
      const last = Number(sessionStorage.getItem(BACKFILL_STORAGE_KEY) || 0)
      if (Number.isFinite(last) && Date.now() - last < BACKFILL_MIN_INTERVAL_MS) return
    } catch {
      /* ignore */
    }

    const keys = periodKeysOverlappingRange(startMs, endMs)
    let cancelled = false

    const run = async () => {
      for (const periodKey of keys) {
        if (cancelled) return
        const [yearStr, monthStr] = periodKey.split('-')
        const year = Number(yearStr)
        const monthIndex0 = Number(monthStr) - 1
        if (!Number.isFinite(year) || !Number.isFinite(monthIndex0)) continue
        const rollup = computeOverviewMonthRollup(year, monthIndex0, vehicles ?? [], expenses ?? [])
        await setDoc(
          doc(db, DASHBOARD_ROLLUPS_COLLECTION, overviewMonthRollupDocId(periodKey)),
          rollup,
          { merge: true },
        )
      }
      try {
        sessionStorage.setItem(BACKFILL_STORAGE_KEY, String(Date.now()))
      } catch {
        /* ignore */
      }
      void queryClient.invalidateQueries({ queryKey: [ROLLUP_QUERY_ROOT] })
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [
    canUseRollup,
    hasRollup,
    userInfo?.isOwner,
    vehicles,
    expenses,
    startMs,
    endMs,
    queryClient,
  ])
}
